import React, { useState, useEffect } from 'react';
import Modal, { ModalBody, ModalHeader, ModalTitle, ModalFooter } from '../../../../components/bootstrap/Modal';
import Button from '../../../../components/bootstrap/Button';
import showNotification from '../../../../components/extras/showNotification';
import BillingValuesDB from '../../../../database/wrappers/billingValues';
import api from '../../../../services/api';

const DiscountModal = ({ isOpen, setIsOpen, data, onSuccess }) => {
  const [items, setItems] = useState([]);
  const [justification, setJustification] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && data) {
      fetchValues();
      setJustification('');
    }
  }, [isOpen, data]);

  const fetchValues = async () => {
    try {
      setLoading(true);
      const db = new BillingValuesDB();
      const billingValues = await db.get('BillingValues');

      const getQuantity = (id) => {
        return data?.adjustments?.items?.[id] || 0;
      };

      const newItems = [
        {
          id: 'system',
          name: 'Sistema',
          price: (billingValues?.system || 0) / 100,
          quantity: getQuantity('system'),
          maxQuantity: data?.items?.system?.qtdInitial || 0
        },
        {
          id: 'pos',
          name: 'POS',
          price: (billingValues?.pos || 0) / 100,
          quantity: getQuantity('pos'),
          maxQuantity: data?.items?.pos?.qtdInitial || 0
        },
        {
          id: 'posDelivery',
          name: 'POS Delivery',
          price: (billingValues?.posDelivery || 0) / 100,
          quantity: getQuantity('posDelivery'),
          maxQuantity: data?.items?.posDelivery?.qtdInitial || 0
        },
        {
          id: 'printer',
          name: 'Impressora',
          price: (billingValues?.printer || 0) / 100,
          quantity: getQuantity('printer'),
          maxQuantity: data?.items?.printer?.qtdInitial || 0
        },
        {
          id: 'totem',
          name: 'Totem',
          price: (billingValues?.totem || 0) / 100,
          quantity: getQuantity('totem'),
          maxQuantity: data?.items?.totem?.qtdInitial || 0
        },
        {
          id: 'tablet',
          name: 'Tablet',
          price: (billingValues?.tablet || 0) / 100,
          quantity: getQuantity('tablet'),
          maxQuantity: data?.items?.tablet?.qtdInitial || 0
        },
      ];
      setItems(newItems);


      if (data?.adjustments?.justification) {
        setJustification(data.adjustments.justification);
      }

    } catch (error) {
      showNotification('Erro', 'Erro ao carregar valores de referência', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const handleIncrement = (id) => {
    setItems(items.map(item => {
      if (item.id === id) {
        const newQuantity = item.quantity + 1;
        return {
          ...item,
          quantity: Math.min(newQuantity, item.maxQuantity)
        };
      }
      return item;
    }));
  };

  const handleDecrement = (id) => {
    setItems(items.map(item =>
      item.id === id ? { ...item, quantity: Math.max(0, item.quantity - 1) } : item
    ));
  };

  const handleDelete = (id) => {
    setItems(items.map(item =>
      item.id === id ? { ...item, quantity: 0 } : item
    ));
  };

  const total = items.reduce((acc, item) => acc + (item.price * item.quantity), 0);

  const handleConfirm = async () => {
    if (!justification.trim()) {
      showNotification('Atenção', 'A justificativa é obrigatória', 'warning');
      return;
    }

    try {
      setLoading(true);
      const payload = {
        billingId: data.id,
        justification: justification,
        items: items.reduce((acc, item) => {
          acc[item.id] = item.quantity;
          return acc;
        }, {})
      };

      await api.put('billing/discount', payload);

      showNotification(
        'Sucesso',
        'Alterações dos descontos foram bem sucedidas',
        'success'
      );
      if (onSuccess) {
        onSuccess();
      }
      setIsOpen(false);
    } catch (error) {
      showNotification('Erro', 'Erro ao aplicar desconto', 'danger');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      titleId="discount-modal"
      isCentered
      size="lg"
    >
      <ModalHeader className="p-4" setIsOpen={setIsOpen}>
        <ModalTitle id="discount-modal-title">Desconto</ModalTitle>
      </ModalHeader>
      <ModalBody>
        <div className="table-responsive">
          <table className="table table-borderless align-middle">
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td style={{ width: '25%' }}>{item.name}</td>
                  <td style={{ width: '20%' }}>{formatCurrency(item.price)}</td>
                  <td style={{ width: '25%' }}>
                    <div className="d-flex align-items-center gap-2">
                      <Button
                        color="dark"
                        size="sm"
                        onClick={() => handleDecrement(item.id)}
                        icon="Remove"
                        className="rounded-2 p-1 px-2"
                        disabled={loading}
                      />
                      <span className="fw-bold" style={{ minWidth: '20px', textAlign: 'center' }}>
                        {String(item.quantity).padStart(2, '0')}
                      </span>
                      <Button
                        color="dark"
                        size="sm"
                        onClick={() => handleIncrement(item.id)}
                        icon="Add"
                        className="rounded-2 p-1 px-2"
                        disabled={loading}
                      />
                    </div>
                  </td>
                  <td style={{ width: '20%' }}>{formatCurrency(item.price * item.quantity)}</td>
                </tr>
              ))}
              <tr>
                <td colSpan="3" className="text-start fw-bold pt-4">Total</td>
                <td colSpan="2" className="text-start fw-bold pt-4">{formatCurrency(total)}</td>
              </tr>
            </tbody>
          </table>
          <div>
            <label className="form-label text-muted small">Justifique a mudança*</label>
            <textarea
              className="form-control"
              rows="4"
              placeholder="Insira a justificativa da mudança"
              value={justification}
              onChange={(e) => setJustification(e.target.value)}
              disabled={loading}
            />
          </div>
        </div>
      </ModalBody>
      <ModalFooter className="p-4">
        <Button icon="Close" color="danger" onClick={() => setIsOpen(false)} disabled={loading}>Cancelar</Button>
        <Button icon="Check" color="success" onClick={handleConfirm} disabled={loading}>
          {loading ? 'Salvando...' : 'Confirmar Alterações'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default DiscountModal;
